/**
 * Cache Coordinator
 *
 * Coordinates caching across multiple layers:
 * - Cross-tab KV cache sharing
 * - Cache invalidation
 * - Distributed cache synchronization
 * - Cache statistics and monitoring
 */

/**
 * Cache layer
 */
export type CacheLayer = 'memory' | 'indexeddb' | 'opfs' | 'broadcast' | 'edge';

/**
 * Cache entry
 */
export interface CacheEntry<T = unknown> {
  /** Cache key */
  key: string;

  /** Cached value */
  value: T;

  /** Created timestamp */
  createdAt: number;

  /** Expires at timestamp (0 = never) */
  expiresAt: number;

  /** Size in bytes */
  sizeBytes: number;

  /** Hit count */
  hitCount: number;

  /** Version for conflict resolution */
  version: number;

  /** Tags for grouped invalidation */
  tags: string[];

  /** Source layer */
  layer: CacheLayer;
}

/**
 * Cache invalidation reason
 */
export type InvalidationReason =
  | 'expired'
  | 'evicted'
  | 'manual'
  | 'tag'
  | 'broadcast'
  | 'version';

/**
 * Cache event
 */
export interface CacheEvent {
  type: 'hit' | 'miss' | 'set' | 'invalidate' | 'sync';
  key: string;
  layer: CacheLayer;
  timestamp: number;
  reason?: InvalidationReason;
}

/**
 * Cache statistics
 */
export interface CacheStats {
  /** Total entries */
  totalEntries: number;

  /** Total size in bytes */
  totalSizeBytes: number;

  /** Hit count */
  hits: number;

  /** Miss count */
  misses: number;

  /** Hit rate (0-1) */
  hitRate: number;

  /** Entries per layer */
  layerStats: Record<
    CacheLayer,
    {
      entries: number;
      sizeBytes: number;
    }
  >;

  /** Eviction count */
  evictions: number;

  /** Average entry age in ms */
  avgAgeMs: number;
}

/**
 * KV cache entry for LLM inference
 */
export interface KVCacheEntry {
  /** Model ID */
  modelId: string;

  /** Prompt hash for cache key */
  promptHash: string;

  /** Key cache tensor */
  keys: Float32Array | ArrayBuffer;

  /** Value cache tensor */
  values: Float32Array | ArrayBuffer;

  /** Sequence length */
  seqLength: number;

  /** Number of layers */
  numLayers: number;

  /** Attention heads */
  numHeads: number;

  /** Head dimension */
  headDim: number;

  /** Created at */
  createdAt: number;

  /** Size in bytes */
  sizeBytes: number;
}

/**
 * Coordinator configuration
 */
export interface CacheCoordinatorConfig {
  /** Enable cross-tab sharing */
  enableCrossTab: boolean;

  /** Broadcast channel name */
  broadcastChannelName: string;

  /** Maximum memory cache size in bytes */
  maxMemoryCacheSize: number;

  /** Maximum entries in memory */
  maxMemoryEntries: number;

  /** Default TTL in ms */
  defaultTTL: number;

  /** Enable persistent cache */
  enablePersistent: boolean;

  /** Persistent cache name */
  persistentCacheName: string;

  /** Sync interval for cross-tab in ms */
  syncInterval: number;

  /** Callback on cache event */
  onCacheEvent?: (event: CacheEvent) => void;

  /** Callback on stats update */
  onStatsUpdate?: (stats: CacheStats) => void;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: CacheCoordinatorConfig = {
  enableCrossTab: true,
  broadcastChannelName: 'edgework_cache_sync',
  maxMemoryCacheSize: 100 * 1024 * 1024, // 100MB
  maxMemoryEntries: 1000,
  defaultTTL: 300000, // 5 minutes
  enablePersistent: true,
  persistentCacheName: 'edgework_cache_v1',
  syncInterval: 5000,
};

/**
 * Generate cache key
 */
function generateKey(prefix: string, ...parts: string[]): string {
  return `${prefix}:${parts.join(':')}`;
}

/**
 * Calculate approximate size of value
 */
function calculateSize(value: unknown): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'string') return value.length * 2;
  if (typeof value === 'number') return 8;
  if (typeof value === 'boolean') return 4;
  if (value instanceof ArrayBuffer) return value.byteLength;
  if (ArrayBuffer.isView(value)) return value.byteLength;
  return JSON.stringify(value).length * 2;
}

/**
 * Memory cache layer
 */
class MemoryCache {
  private cache: Map<string, CacheEntry> = new Map();
  private maxSize: number;
  private maxEntries: number;
  private currentSize = 0;

  constructor(maxSize: number, maxEntries: number) {
    this.maxSize = maxSize;
    this.maxEntries = maxEntries;
  }

  get<T>(key: string): CacheEntry<T> | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    if (!entry) return null;

    // Check expiration
    if (entry.expiresAt > 0 && entry.expiresAt < Date.now()) {
      this.delete(key);
      return null;
    }

    entry.hitCount++;
    return entry;
  }

  set<T>(
    key: string,
    value: T,
    options: {
      ttl?: number;
      tags?: string[];
      version?: number;
    } = {}
  ): CacheEntry<T> {
    // Remove existing
    if (this.cache.has(key)) {
      this.delete(key);
    }

    const sizeBytes = calculateSize(value);

    // Evict if necessary
    while (
      (this.currentSize + sizeBytes > this.maxSize ||
        this.cache.size >= this.maxEntries) &&
      this.cache.size > 0
    ) {
      this.evictLRU();
    }

    const entry: CacheEntry<T> = {
      key,
      value,
      createdAt: Date.now(),
      expiresAt: options.ttl ? Date.now() + options.ttl : 0,
      sizeBytes,
      hitCount: 0,
      version: options.version || 1,
      tags: options.tags || [],
      layer: 'memory',
    };

    this.cache.set(key, entry as CacheEntry);
    this.currentSize += sizeBytes;

    return entry;
  }

  delete(key: string): boolean {
    const entry = this.cache.get(key);
    if (entry) {
      this.currentSize -= entry.sizeBytes;
      this.cache.delete(key);
      return true;
    }
    return false;
  }

  private evictLRU(): void {
    // Find entry with lowest hit count
    let lruKey: string | null = null;
    let lruHits = Infinity;

    for (const [key, entry] of this.cache) {
      if (entry.hitCount < lruHits) {
        lruHits = entry.hitCount;
        lruKey = key;
      }
    }

    if (lruKey) {
      this.delete(lruKey);
    }
  }

  invalidateByTag(tag: string): number {
    let count = 0;
    for (const [key, entry] of this.cache) {
      if (entry.tags.includes(tag)) {
        this.delete(key);
        count++;
      }
    }
    return count;
  }

  clear(): void {
    this.cache.clear();
    this.currentSize = 0;
  }

  getStats(): { entries: number; sizeBytes: number } {
    return {
      entries: this.cache.size,
      sizeBytes: this.currentSize,
    };
  }

  entries(): IterableIterator<[string, CacheEntry]> {
    return this.cache.entries();
  }
}

/**
 * Cache Coordinator
 *
 * Coordinates caching across memory, persistent storage, and tabs.
 */
export class CacheCoordinator {
  private config: CacheCoordinatorConfig;
  private memoryCache: MemoryCache;
  private broadcastChannel: BroadcastChannel | null = null;
  private syncInterval: ReturnType<typeof setInterval> | null = null;
  private stats: CacheStats;
  private kvCache: Map<string, KVCacheEntry> = new Map();

  constructor(config: Partial<CacheCoordinatorConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.memoryCache = new MemoryCache(
      this.config.maxMemoryCacheSize,
      this.config.maxMemoryEntries
    );
    this.stats = this.createEmptyStats();
  }

  /**
   * Create empty stats
   */
  private createEmptyStats(): CacheStats {
    return {
      totalEntries: 0,
      totalSizeBytes: 0,
      hits: 0,
      misses: 0,
      hitRate: 0,
      layerStats: {
        memory: { entries: 0, sizeBytes: 0 },
        indexeddb: { entries: 0, sizeBytes: 0 },
        opfs: { entries: 0, sizeBytes: 0 },
        broadcast: { entries: 0, sizeBytes: 0 },
        edge: { entries: 0, sizeBytes: 0 },
      },
      evictions: 0,
      avgAgeMs: 0,
    };
  }

  /**
   * Initialize the coordinator
   */
  async initialize(): Promise<void> {
    // Set up broadcast channel for cross-tab communication
    if (this.config.enableCrossTab && 'BroadcastChannel' in globalThis) {
      this.broadcastChannel = new BroadcastChannel(
        this.config.broadcastChannelName
      );
      this.broadcastChannel.onmessage = (event) =>
        this.handleBroadcastMessage(event.data);
    }

    // Start sync interval
    if (this.config.syncInterval > 0) {
      this.syncInterval = setInterval(
        () => this.syncStats(),
        this.config.syncInterval
      );
    }
  }

  /**
   * Shutdown the coordinator
   */
  async shutdown(): Promise<void> {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }

    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
  }

  /**
   * Get a cached value
   */
  async get<T>(
    key: string,
    options: {
      layers?: CacheLayer[];
    } = {}
  ): Promise<T | null> {
    const layers = options.layers || ['memory', 'indexeddb'];

    for (const layer of layers) {
      const entry = await this.getFromLayer<T>(key, layer);
      if (entry) {
        this.stats.hits++;
        this.emitEvent({ type: 'hit', key, layer, timestamp: Date.now() });
        return entry.value;
      }
    }

    this.stats.misses++;
    this.emitEvent({
      type: 'miss',
      key,
      layer: 'memory',
      timestamp: Date.now(),
    });
    return null;
  }

  /**
   * Get from specific layer
   */
  private async getFromLayer<T>(
    key: string,
    layer: CacheLayer
  ): Promise<CacheEntry<T> | null> {
    switch (layer) {
      case 'memory':
        return this.memoryCache.get<T>(key);

      case 'indexeddb':
        return this.getFromIndexedDB<T>(key);

      case 'opfs':
        return this.getFromOPFS<T>(key);

      default:
        return null;
    }
  }

  /**
   * Get from IndexedDB
   */
  private async getFromIndexedDB<T>(
    key: string
  ): Promise<CacheEntry<T> | null> {
    if (!this.config.enablePersistent) return null;

    try {
      const db = await this.openDB();
      const tx = db.transaction('cache', 'readonly');
      const store = tx.objectStore('cache');

      return new Promise((resolve, reject) => {
        const request = store.get(key);
        request.onsuccess = () => {
          const entry = request.result as CacheEntry<T> | undefined;
          if (
            entry &&
            (entry.expiresAt === 0 || entry.expiresAt > Date.now())
          ) {
            resolve(entry);
          } else {
            resolve(null);
          }
        };
        request.onerror = () => reject(request.error);
      });
    } catch {
      return null;
    }
  }

  /**
   * Get from OPFS
   */
  private async getFromOPFS<T>(key: string): Promise<CacheEntry<T> | null> {
    if (!('storage' in navigator && 'getDirectory' in navigator.storage)) {
      return null;
    }

    try {
      const root = await navigator.storage.getDirectory();
      const cacheDir = await root.getDirectoryHandle('cache', { create: true });
      const fileHandle = await cacheDir.getFileHandle(`${key}.json`);
      const file = await fileHandle.getFile();
      const text = await file.text();
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  /**
   * Set a cached value
   */
  async set<T>(
    key: string,
    value: T,
    options: {
      ttl?: number;
      tags?: string[];
      layers?: CacheLayer[];
      broadcast?: boolean;
    } = {}
  ): Promise<void> {
    const layers = options.layers || ['memory'];
    const ttl = options.ttl || this.config.defaultTTL;

    for (const layer of layers) {
      await this.setInLayer(key, value, { ...options, ttl }, layer);
    }

    this.emitEvent({
      type: 'set',
      key,
      layer: layers[0],
      timestamp: Date.now(),
    });

    // Broadcast to other tabs
    if (options.broadcast !== false && this.broadcastChannel) {
      this.broadcastChannel.postMessage({
        type: 'cache_set',
        key,
        value,
        options: { ttl, tags: options.tags },
      });
    }
  }

  /**
   * Set in specific layer
   */
  private async setInLayer<T>(
    key: string,
    value: T,
    options: { ttl?: number; tags?: string[] },
    layer: CacheLayer
  ): Promise<void> {
    switch (layer) {
      case 'memory':
        this.memoryCache.set(key, value, options);
        break;

      case 'indexeddb':
        await this.setInIndexedDB(key, value, options);
        break;

      case 'opfs':
        await this.setInOPFS(key, value, options);
        break;
    }
  }

  /**
   * Set in IndexedDB
   */
  private async setInIndexedDB<T>(
    key: string,
    value: T,
    options: { ttl?: number; tags?: string[] }
  ): Promise<void> {
    if (!this.config.enablePersistent) return;

    const entry: CacheEntry<T> = {
      key,
      value,
      createdAt: Date.now(),
      expiresAt: options.ttl ? Date.now() + options.ttl : 0,
      sizeBytes: calculateSize(value),
      hitCount: 0,
      version: 1,
      tags: options.tags || [],
      layer: 'indexeddb',
    };

    const db = await this.openDB();
    const tx = db.transaction('cache', 'readwrite');
    const store = tx.objectStore('cache');

    return new Promise((resolve, reject) => {
      const request = store.put(entry);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Set in OPFS
   */
  private async setInOPFS<T>(
    key: string,
    value: T,
    options: { ttl?: number; tags?: string[] }
  ): Promise<void> {
    if (!('storage' in navigator && 'getDirectory' in navigator.storage)) {
      return;
    }

    const entry: CacheEntry<T> = {
      key,
      value,
      createdAt: Date.now(),
      expiresAt: options.ttl ? Date.now() + options.ttl : 0,
      sizeBytes: calculateSize(value),
      hitCount: 0,
      version: 1,
      tags: options.tags || [],
      layer: 'opfs',
    };

    const root = await navigator.storage.getDirectory();
    const cacheDir = await root.getDirectoryHandle('cache', { create: true });
    const fileHandle = await cacheDir.getFileHandle(`${key}.json`, {
      create: true,
    });
    const writable = await fileHandle.createWritable();
    await writable.write(JSON.stringify(entry));
    await writable.close();
  }

  /**
   * Open IndexedDB
   */
  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.config.persistentCacheName, 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('cache')) {
          db.createObjectStore('cache', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('kv_cache')) {
          db.createObjectStore('kv_cache', { keyPath: 'promptHash' });
        }
      };
    });
  }

  /**
   * Invalidate a cached value
   */
  async invalidate(
    key: string,
    options: {
      layers?: CacheLayer[];
      broadcast?: boolean;
      reason?: InvalidationReason;
    } = {}
  ): Promise<void> {
    const layers = options.layers || ['memory', 'indexeddb', 'opfs'];

    for (const layer of layers) {
      await this.invalidateInLayer(key, layer);
    }

    this.emitEvent({
      type: 'invalidate',
      key,
      layer: layers[0],
      timestamp: Date.now(),
      reason: options.reason || 'manual',
    });

    // Broadcast to other tabs
    if (options.broadcast !== false && this.broadcastChannel) {
      this.broadcastChannel.postMessage({
        type: 'cache_invalidate',
        key,
        reason: options.reason || 'manual',
      });
    }
  }

  /**
   * Invalidate in specific layer
   */
  private async invalidateInLayer(
    key: string,
    layer: CacheLayer
  ): Promise<void> {
    switch (layer) {
      case 'memory':
        this.memoryCache.delete(key);
        break;

      case 'indexeddb':
        await this.deleteFromIndexedDB(key);
        break;

      case 'opfs':
        await this.deleteFromOPFS(key);
        break;
    }
  }

  /**
   * Delete from IndexedDB
   */
  private async deleteFromIndexedDB(key: string): Promise<void> {
    if (!this.config.enablePersistent) return;

    const db = await this.openDB();
    const tx = db.transaction('cache', 'readwrite');
    const store = tx.objectStore('cache');

    return new Promise((resolve, reject) => {
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete from OPFS
   */
  private async deleteFromOPFS(key: string): Promise<void> {
    if (!('storage' in navigator && 'getDirectory' in navigator.storage)) {
      return;
    }

    try {
      const root = await navigator.storage.getDirectory();
      const cacheDir = await root.getDirectoryHandle('cache');
      await cacheDir.removeEntry(`${key}.json`);
    } catch {
      // File doesn't exist
    }
  }

  /**
   * Invalidate by tag
   */
  async invalidateByTag(
    tag: string,
    options: {
      layers?: CacheLayer[];
      broadcast?: boolean;
    } = {}
  ): Promise<number> {
    let count = 0;

    // Memory cache
    count += this.memoryCache.invalidateByTag(tag);

    // Broadcast to other tabs
    if (options.broadcast !== false && this.broadcastChannel) {
      this.broadcastChannel.postMessage({
        type: 'cache_invalidate_tag',
        tag,
      });
    }

    return count;
  }

  /**
   * Store KV cache for LLM inference
   */
  async storeKVCache(entry: KVCacheEntry): Promise<void> {
    const key = generateKey('kv', entry.modelId, entry.promptHash);
    this.kvCache.set(key, entry);

    // Also persist to IndexedDB
    if (this.config.enablePersistent) {
      try {
        const db = await this.openDB();
        const tx = db.transaction('kv_cache', 'readwrite');
        const store = tx.objectStore('kv_cache');
        await new Promise<void>((resolve, reject) => {
          const request = store.put(entry);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      } catch {
        // Silently fail for persistence
      }
    }
  }

  /**
   * Get KV cache for LLM inference
   */
  async getKVCache(
    modelId: string,
    promptHash: string
  ): Promise<KVCacheEntry | null> {
    const key = generateKey('kv', modelId, promptHash);

    // Check memory first
    const memEntry = this.kvCache.get(key);
    if (memEntry) {
      return memEntry;
    }

    // Check IndexedDB
    if (this.config.enablePersistent) {
      try {
        const db = await this.openDB();
        const tx = db.transaction('kv_cache', 'readonly');
        const store = tx.objectStore('kv_cache');
        return new Promise((resolve, reject) => {
          const request = store.get(promptHash);
          request.onsuccess = () => resolve(request.result || null);
          request.onerror = () => reject(request.error);
        });
      } catch {
        return null;
      }
    }

    return null;
  }

  /**
   * Handle broadcast message
   */
  private handleBroadcastMessage(data: {
    type: string;
    key?: string;
    value?: unknown;
    options?: { ttl?: number; tags?: string[] };
    tag?: string;
    reason?: InvalidationReason;
  }): void {
    switch (data.type) {
      case 'cache_set':
        if (data.key && data.value !== undefined) {
          this.memoryCache.set(data.key, data.value, data.options);
        }
        break;

      case 'cache_invalidate':
        if (data.key) {
          this.memoryCache.delete(data.key);
        }
        break;

      case 'cache_invalidate_tag':
        if (data.tag) {
          this.memoryCache.invalidateByTag(data.tag);
        }
        break;
    }

    this.emitEvent({
      type: 'sync',
      key: data.key || '',
      layer: 'broadcast',
      timestamp: Date.now(),
      reason: data.reason,
    });
  }

  /**
   * Emit cache event
   */
  private emitEvent(event: CacheEvent): void {
    this.config.onCacheEvent?.(event);
  }

  /**
   * Sync stats
   */
  private syncStats(): void {
    const memStats = this.memoryCache.getStats();

    this.stats.layerStats.memory = memStats;
    this.stats.totalEntries = memStats.entries;
    this.stats.totalSizeBytes = memStats.sizeBytes;
    this.stats.hitRate =
      this.stats.hits + this.stats.misses > 0
        ? this.stats.hits / (this.stats.hits + this.stats.misses)
        : 0;

    this.config.onStatsUpdate?.(this.stats);
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    this.syncStats();
    return { ...this.stats };
  }

  /**
   * Clear all caches
   */
  async clear(): Promise<void> {
    this.memoryCache.clear();
    this.kvCache.clear();

    if (this.config.enablePersistent) {
      try {
        const db = await this.openDB();

        const tx1 = db.transaction('cache', 'readwrite');
        tx1.objectStore('cache').clear();

        const tx2 = db.transaction('kv_cache', 'readwrite');
        tx2.objectStore('kv_cache').clear();
      } catch {
        // Silently fail
      }
    }

    // Broadcast clear
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage({ type: 'cache_clear' });
    }
  }
}

/**
 * Pre-configured coordinator presets
 */
export const CACHE_PRESETS = {
  /** High performance with large memory cache */
  performance: {
    maxMemoryCacheSize: 500 * 1024 * 1024, // 500MB
    maxMemoryEntries: 5000,
    defaultTTL: 600000, // 10 minutes
    enableCrossTab: true,
    syncInterval: 2000,
  } as Partial<CacheCoordinatorConfig>,

  /** Memory efficient */
  lowMemory: {
    maxMemoryCacheSize: 50 * 1024 * 1024, // 50MB
    maxMemoryEntries: 500,
    defaultTTL: 60000, // 1 minute
    enableCrossTab: false,
    syncInterval: 10000,
  } as Partial<CacheCoordinatorConfig>,

  /** Balanced */
  balanced: {
    maxMemoryCacheSize: 100 * 1024 * 1024, // 100MB
    maxMemoryEntries: 1000,
    defaultTTL: 300000, // 5 minutes
    enableCrossTab: true,
    syncInterval: 5000,
  } as Partial<CacheCoordinatorConfig>,
};
